import React, { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { useRoutePreloader } from '@/hooks/useRoutePreloader';

interface PreloadLinkProps extends LinkProps {
  /**
   * 是否启用预加载，默认为 true
   */
  enablePreload?: boolean;
  /**
   * 预加载延迟时间（毫秒），默认 200ms
   */
  preloadDelay?: number;
  /**
   * 子元素
   */
  children: React.ReactNode;
}

/**
 * 支持预加载的 Link 组件
 * 在鼠标悬停时预加载目标路由组件，提升页面跳转体验
 */
const PreloadLink = forwardRef<HTMLAnchorElement, PreloadLinkProps>(
  ({ enablePreload = true, preloadDelay = 200, to, children, ...props }, ref) => {
    const { preloadRouteWithDelay, cancelPreload } = useRoutePreloader();

    // 获取目标路径
    const targetPath = typeof to === 'string' ? to : to.pathname || '';

    const handleMouseEnter = () => {
      if (enablePreload && targetPath) {
        preloadRouteWithDelay(targetPath, preloadDelay);
      }
    };

    const handleMouseLeave = () => {
      if (enablePreload) {
        cancelPreload();
      }
    };

    return (
      <Link
        ref={ref}
        to={to}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {children}
      </Link>
    );
  }
);

PreloadLink.displayName = 'PreloadLink';

export default PreloadLink;
